'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface HoverEffectProps {
  children: React.ReactNode
  className?: string
}

export function HoverCard({ children, className = '' }: HoverEffectProps) {
  const [isHovered, setIsHovered] = useState(false)
  
  return (
    <motion.div
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      {/* 背景光效 */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-orange-500/20 via-purple-500/20 to-blue-500/20"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: isHovered ? 1 : 0, 
          scale: isHovered ? 1 : 0.8 
        }}
        transition={{ duration: 0.3 }}
      />
      
      {/* 边框光效 */}
      <motion.div
        className="absolute inset-0 border border-white/20"
        animate={{ 
          borderColor: isHovered ? 'rgba(249, 115, 22, 0.5)' : 'rgba(255, 255, 255, 0.2)'
        }}
        transition={{ duration: 0.3 }}
      />
      
      {/* 内容 */}
      <div className="relative z-10">
        {children}
      </div>
      
      {/* 粒子效果 */}
      <AnimatePresence>
        {isHovered && (
          <>
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-orange-400 rounded-full"
                initial={{ 
                  x: Math.random() * 100 - 50,
                  y: Math.random() * 100 - 50,
                  opacity: 0,
                  scale: 0
                }}
                animate={{ 
                  x: Math.random() * 200 - 100,
                  y: Math.random() * 200 - 100,
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0]
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ 
                  duration: 2,
                  delay: i * 0.1,
                  repeat: Infinity
                }}
                style={{
                  left: '50%',
                  top: '50%'
                }}
              />
            ))}
          </>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export function HoverButton({ children, className = '', onClick, ...props }: HoverEffectProps & { onClick?: () => void }) {
  const [isHovered, setIsHovered] = useState(false)
  
  return (
    <motion.button
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      {...props}
    >
      {/* 背景动画 */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-orange-600 via-purple-600 to-blue-600"
        animate={{
          backgroundPosition: isHovered ? '100% 0' : '0% 0'
        }}
        transition={{ duration: 0.5 }}
        style={{
          backgroundSize: '200% 100%'
        }}
      />
      
      {/* 光效覆盖 */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
        initial={{ x: '-100%' }}
        animate={{ x: isHovered ? '100%' : '-100%' }}
        transition={{ duration: 0.6 }}
      />
      
      {/* 脉冲效果 */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            className="absolute inset-0 border-2 border-orange-400 rounded-lg"
            initial={{ scale: 1, opacity: 1 }}
            animate={{ scale: 1.2, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, repeat: Infinity }}
          />
        )}
      </AnimatePresence>
      
      {/* 内容 */}
      <span className="relative z-10">
        {children}
      </span>
    </motion.button>
  )
}

export function HoverText({ children, className = '' }: HoverEffectProps) {
  const [isHovered, setIsHovered] = useState(false)
  
  return (
    <motion.span
      className="relative inline-block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 文字光效 - 悬停时显示 */}
      <motion.span
        className={`absolute inset-0 text-transparent bg-gradient-to-r from-orange-400 via-purple-400 to-blue-400 bg-clip-text ${className}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.span>
      
      {/* 原始文字 - 默认显示，使用传入的样式 */}
      <span className={`relative z-10 ${className}`}>
        {children}
      </span>
      
      {/* 下划线动画 */}
      <motion.span
        className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-orange-500 to-purple-500"
        initial={{ width: 0 }}
        animate={{ width: isHovered ? '100%' : '0%' }}
        transition={{ duration: 0.3 }}
      />
    </motion.span>
  )
}